import { Component, ElementRef, Inject, Input, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { ObservationsService } from '../../../services/Observations/observations.service';
import { ProjectsService } from '../../../services/projects.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ObservationDetailsComponent } from '../../../modules/observations-manager/observation-details/observation-details.component';
import {
  FormControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { GlobalService } from '../../../services/global.service';
import { TranslocoService } from '@ngneat/transloco';
import { Project360Service } from '../../../modules/p-dashboard/project360.service';
import { PanosService } from '../../../modules/project/panos.service';
import { GAnalyticsService } from '../../../services/g-analytics.service';
import { ActiveMember } from '../../../models/members.model';
import { MatDatepicker } from '@angular/material/datepicker';
import moment, { Moment } from 'moment';
import { GStatic } from '../../../services/GStatic';
import { firstValueFrom, Observable, Subject, takeUntil, BehaviorSubject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from '../confirm-dialog/confirm-dialog.component';

import { CustomField } from '../../../models/custom-fields.model';
import { ObservationCustomFieldsService } from '../../../services/observation-custom-fields.service';
import { QuillEditorService, EditorConfig } from '../../../services/quill-editor.service';
import Quill from 'quill';
import { UserService } from 'app/core/user/user.service';
import { appendObservationHashtagsToFormData } from '../../utils/observation-form.utils';
import {
  ObservationCategory,
  ObservationCategoryOption,
  ObservationForm,
} from 'app/models/observations.model';
import { getUploadPlaceholderImage } from '../../utils/branded-placeholder.util';

@Component({
  selector: 'app-observation-on-document-pop-up',
  templateUrl: './observation-on-document-pop-up.component.html',
  styleUrls: ['./observation-on-document-pop-up.component.scss'],
})
export class ObservationOnDocumentPopUpComponent implements OnInit, OnDestroy {
  privacy: any;

  fileOptions = {
    maxFilesLimit: 10,
    maxFileSize: 200,
    allowedExtensions: [],
  };

  nwc_client: boolean = false;

  attachments = [];
  observationHashtags: string[] = [];
  attachmentsError: string | null = null;
  observationCategories: ObservationCategory[] = [];
  observationChildCategories: ObservationCategory[] = [];
  observationSignificances = [];
  observationTypes = [];
  projectActiveMembers: ActiveMember[];
  observation = [];
  observationForm!: ObservationForm;
  screenShotImage: any;
  observationDetails: any;
  isLoading = true;
  selectedType: boolean = true;
  currentDueDate = new UntypedFormControl();
  formattedDate: string = '';
  selected = {
    name: 'Red',
    color: 'red',
  };
  @ViewChild(MatDatepicker)
  picker: MatDatepicker<Moment>;
  @ViewChild('screenshot') screenshot: any;
  @ViewChild('screenshot2') myCanvas: any;
  @ViewChild('fileAttached') fileAttached: ElementRef;
  ctx: CanvasRenderingContext2D;
  pos = { x: 0, y: 0 };
  @Input() minDate: Date;
  permissions = { change_status: false, create_reply: false };
  fileToUpload: any;

  searchControl = new FormControl('');
  filteredUsers: ActiveMember[];
  protected _onDestroy = new Subject<void>();

  // Custom Fields Properties
  customFields$ = new BehaviorSubject<CustomField[]>([]);
  isLoadingCustomFields$ = new BehaviorSubject<boolean>(false);
  private observationCategoriesRequestId = 0;

  observationCodes = [];
  configurableFields: any[] = [];
  quillConfig = {
    toolbar: [
      ['bold'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      // [{ align: [] }],
      // [{ direction: 'rtl' }],
      ['clean'],
    ],
  };

  // Compare function for mat-select to match string/number ids
  compareWithId = (a: any, b: any): boolean => {
    return a !== undefined && a !== null && b !== undefined && b !== null
      ? String(a) === String(b)
      : a === b;
  };

  // Get title max length based on client type
  get titleMaxLength(): number {
    return this.nwc_client ? 128 : 200;
  }

  // Quill editor instances - populated via onEditorCreated callback
  descriptionEditor: Quill | null = null;
  recommendedActionEditor: Quill | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private observationsService: ObservationsService,
    private projectService: ProjectsService,
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<ObservationDetailsComponent>,
    private fb: UntypedFormBuilder,
    private _toaster: ToastrService,
    private globalService: GlobalService,
    public translocoService: TranslocoService,
    public _project: Project360Service,
    public _pano: PanosService,
    private googleServices: GAnalyticsService,
    private observationCustomFields: ObservationCustomFieldsService,
    private userService: UserService,
    private quillEditorService: QuillEditorService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.nwc_client = this._project.isNwcClientSync();
    this.initForm();
    await this.getObservationSignificancesAndTypes();
    await this.getProjectActiveMembers();
    this.isLoading = false;
    this.fileOptions.allowedExtensions = this._project.allowedExtensions;

    // Initialize custom fields properties for all clients
    const customFieldsProps = this.observationCustomFields.initializeCustomFieldsProperties();
    this.customFields$ = customFieldsProps.customFields$;
    this.isLoadingCustomFields$ = customFieldsProps.isLoadingCustomFields$;

    // Add form validation debugging
    this.setupFormDebugging();

    //Add search to users list
    this.searchControl.valueChanges
      .pipe(debounceTime(700), takeUntil(this._onDestroy))
      .subscribe((val) => {
        if (val.toString().length > 0) {
          this.filteredUsers = this.projectActiveMembers.filter(
            (user) =>
              user.name.toLowerCase().includes(val.toLowerCase()) ||
              user.email.toLowerCase().includes(val.toLowerCase()),
          );
        } else if (val.toString().length === 0) {
          this.filteredUsers = [...this.projectActiveMembers];
        }
      });

    // Disable observation type when custom fields are loading
    this.isLoadingCustomFields$.pipe(takeUntil(this._onDestroy)).subscribe((isLoading) => {
      const typeControl = this.observationForm.get('type');
      if (isLoading) {
        typeControl?.disable();
      } else {
        typeControl?.enable();
      }
    });

    // Watch for description control status changes to disable/enable Quill editor
    this.observationForm
      .get('description')
      ?.statusChanges.pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.updateQuillEditorState();
      });
  }

  /**
   * Update Quill editor state based on form control disabled status
   * This ensures the visual appearance matches the disabled state
   */
  private updateQuillEditorState(): void {
    const descriptionControl = this.observationForm.get('description');

    // Check if both the editor and control exist, and the editor has the enable method
    if (
      this.descriptionEditor &&
      descriptionControl &&
      typeof this.descriptionEditor.enable === 'function'
    ) {
      const isDisabled = descriptionControl.disabled;
      this.descriptionEditor.enable(!isDisabled);

      // Add/remove visual disabled styling to match Material UI disabled state
      const editorContainer = this.descriptionEditor.container;
      if (editorContainer) {
        if (isDisabled) {
          editorContainer.style.opacity = '0.5';
          editorContainer.style.pointerEvents = 'none';
          editorContainer.style.cursor = 'not-allowed';
          // Don't change background color - keep the existing dark theme
        } else {
          editorContainer.style.opacity = '1';
          editorContainer.style.pointerEvents = 'auto';
          editorContainer.style.cursor = 'text';
        }
      }
    }
  }

  initForm(): void {
    if (!this.data) {
      console.warn('Data not available for form initialization');
      return;
    }

    this._setMinDate();
    const formControls: any = {
      project_id: [this.data.project_id, Validators.required],
      report_id: [this.data.report_id],
      floor_id: [this.data.floor_id],
      point_id: [this.data.point_id],
      location_x: [this.data.location_x],
      location_y: [this.data.location_y],
      title: [null, Validators.required],
      description: [null, this.nwc_client ? this.descriptionRequiredValidator() : null],
      recommended_corrective_action: [null],
      observation_flag: ['observation_on_document'],
      screen_shot: [null],
      categories: [this.nwc_client ? null : [], Validators.required],
      child_categories: [this.nwc_client ? null : [], this.subcategoryRequiredValidator()],
      users: [null, this.nwc_client ? null : Validators.required],
      due_date: [null],
      code: ['none'], //OBS code with default value N/A => none
      nwc_client: [this.nwc_client],
    };

    // Conditionally add fields based on configurable_fields visibility
    if (this.isFieldVisible('type')) {
      formControls.type = [null, Validators.required];
    }
    if (this.isFieldVisible('privacy')) {
      formControls.privacy = ['public', Validators.required];
    }
    if (this.isFieldVisible('significance')) {
      formControls.significance = [null];
    }
    if (this.isFieldVisible('due_date')) {
      formControls.due_date = [null];
    }
    if (this.isFieldVisible('code')) {
      formControls.code = ['none']; //OBS code with default value N/A => none
    }

    this.observationForm = this.fb.group(formControls);
  }

  /**
   * Check if a field is visible based on configurable_fields
   * Returns true if field is visible, false if hidden or not configured
   */
  isFieldVisible(fieldName: string): boolean {
    if (!this.configurableFields || this.configurableFields.length === 0) {
      // If no configurable_fields, show all fields by default
      return true;
    }
    const fieldConfig = this.configurableFields.find((field) => field.attribute === fieldName);
    // If field is not in configurable_fields, show it by default
    // If field is in configurable_fields, use its visible property
    return fieldConfig ? fieldConfig.visible !== false : true;
  }

  setupFormDebugging(): void {
    // Subscribe to form value changes to debug validation
    if (this.observationForm) {
      this.observationForm.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe((formValue) => {
        // this.debugFormValidation(formValue);
      });
    }
  }

  debugFormValidation(formValue: any): void {
    if (!this.observationForm) {
      console.warn('Form not available for validation debugging');
      return;
    }

    const titleValue = formValue.title;
    const titleTrimmed = titleValue?.trim();
    const formValid = this.observationForm.valid;
    const isLoading = this.isLoading;

    const disabledCondition = !formValid || isLoading || !titleTrimmed;

    console.group('🔍 Form Validation Debug');
    console.log('Form Value:', formValue);
    console.log('Title Value:', titleValue);
    console.log('Title Trimmed:', titleTrimmed);
    console.log('Title Length:', titleValue?.length);
    console.log('Form Valid:', formValid);
    console.log('Is Loading:', isLoading);
    console.log('Disabled Condition:', disabledCondition);

    // Check individual form controls
    console.group('Form Controls Status:');
    Object.keys(this.observationForm.controls).forEach((controlName) => {
      const control = this.observationForm.get(controlName);
      console.log(`${controlName}:`, {
        value: control.value,
        valid: control.valid,
        invalid: control.invalid,
        errors: control.errors,
        touched: control.touched,
        dirty: control.dirty,
      });
    });
    console.groupEnd();

    console.groupEnd();
  }

  getType(type): void {
    const observationType = type?.value;
    this.attachmentsError = null;

    if (!observationType) {
      this.customFields$.next([]);
      this.isLoadingCustomFields$.next(false);
      return;
    }

    GStatic.log('type: ' + observationType);

    if (observationType !== 'note') {
      this.googleServices.setEvent(
        'AddNewObservation_SelectObservationType_project',
        'Viewer Interactivity',
      );
      this.selectedType = true;
    } else {
      this.googleServices.setEvent(
        'AddNewObservation_SelectNoteType_project',
        'Viewer Interactivity',
      );
      this.formattedDate = '';
      this.selectedType = false;
    }

    this.observationCustomFields.resetObservationCategoryState(
      this.observationForm,
      this.nwc_client,
      this.nwc_client,
    );
    this.observationChildCategories = [];
    this.updateChildCategoryValidation();

    // Handle observation type change (custom fields and categories)
    // API will be called and we handle empty/error responses gracefully
    this.observationCustomFields.handleObservationTypeChange(
      observationType,
      this.observationForm,
      this.customFields$,
      this.isLoadingCustomFields$,
      undefined, // onCategoriesLoaded
      (error) => {
        // Handle custom fields API error gracefully
        console.warn('Custom fields API error (non-critical):', error);
        // Form remains usable without custom fields
      },
      this.nwc_client, // Pass NWC client flag to preserve categories for non-NWC clients
    );

    // Load categories for the selected observation type
    this.getObservationCategories();
  }

  resetPicker() {
    this.currentDueDate = new UntypedFormControl(null);
    this.formattedDate = '';
  }

  async getObservationCategories(): Promise<void> {
    const currentObservationType = this.observationForm.get('type')?.value;
    const requestId = ++this.observationCategoriesRequestId;

    try {
      const res: any = await firstValueFrom(
        this.observationsService.observationCategories(currentObservationType),
      );
      if (
        requestId !== this.observationCategoriesRequestId ||
        currentObservationType !== this.observationForm.get('type')?.value
      ) {
        return;
      }

      this.observationCategories = this.observationCustomFields.normalizeObservationCategories(
        res?.observation_categories,
      );
      this.observationCustomFields.syncSelectedObservationCategories(
        this.observationForm,
        this.observationCategories,
        this.nwc_client,
      );
      const categoriesOptions: ObservationCategoryOption[] =
        this.observationCustomFields.buildObservationCategoryOptions(
          this.observationCategories,
          this.translocoService.getActiveLang(),
        );
      this.observationForm.metadata = {
        ...this.observationForm.metadata,
        categories: categoriesOptions,
      };

      // Don't concatenate all children here - we'll filter based on selected category
      this.updateChildCategories();
    } catch (error) {
      console.error('Error fetching observation categories:', error);
    }
  }

  updateChildCategories(): void {
    const selectedCategories = this.observationForm.get('categories')?.value;
    this.observationChildCategories = this.observationCustomFields.getObservationChildCategories(
      this.observationCategories,
      selectedCategories,
      this.nwc_client,
    );

    // Update validation for child categories after updating the available options
    this.updateChildCategoryValidation();
  }

  onCategorySelectionChange(): void {
    this.observationCustomFields.resetObservationCategoryState(
      this.observationForm,
      this.nwc_client,
    );
    this.updateChildCategories();
  }

  shouldShowSubcategoryField(): boolean {
    return this.observationCustomFields.shouldShowObservationSubcategoryField(
      this.observationForm,
      this.observationChildCategories,
      this.nwc_client,
      this.observationCategories,
    );
  }

  /**
   * Custom validator for child_categories that makes it required when subcategories are available
   */
  subcategoryRequiredValidator() {
    return (control: UntypedFormControl) => {
      // If there are no child categories available, no validation needed
      if (!this.observationChildCategories || this.observationChildCategories.length === 0) {
        return null;
      }

      // If child categories are available, the field is required
      const value = control.value;
      if (this.nwc_client) {
        // Single selection mode
        if (!value || value === null || value === undefined) {
          return { subcategoryRequired: true };
        }
      } else {
        // Multiple selection mode
        if (!value || !Array.isArray(value) || value.length === 0) {
          return { subcategoryRequired: true };
        }
      }

      return null;
    };
  }

  /**
   * Update child category validation when categories change
   */
  updateChildCategoryValidation(): void {
    const childCategoriesControl = this.observationForm.get('child_categories');
    if (childCategoriesControl) {
      // Re-apply the validator
      childCategoriesControl.setValidators(this.subcategoryRequiredValidator());
      childCategoriesControl.updateValueAndValidity();
    }
  }

  /**
   * Custom validator for description field that handles Quill editor HTML content
   * Validates that the description is not empty or whitespace-only
   */
  descriptionRequiredValidator() {
    return (control: UntypedFormControl) => {
      if (!this.nwc_client) {
        return null; // Not required for non-NWC clients
      }

      const value = control.value;

      // Handle null/undefined
      if (!value || value === null || value === undefined) {
        return { descriptionRequired: true };
      }

      // Strip HTML tags and extract plain text
      const textContent =
        typeof value === 'string'
          ? value.replace(/<[^>]*>/g, '').trim()
          : String(value)
              .replace(/<[^>]*>/g, '')
              .trim();

      // Check if empty after stripping HTML and trimming whitespace
      if (textContent === '' || textContent.length === 0) {
        return { descriptionRequired: true };
      }

      return null;
    };
  }

  async getObservationSignificancesAndTypes(): Promise<void> {
    try {
      const res: any = await this.observationsService
        .observationSignificancesAndTypes()
        .toPromise();

      // Filter observation types based on NWC client status
      this.observationTypes = res?.data?.observation_types || [];

      this.observationSignificances = res?.data?.observation_significance || [];
      this.observationCodes = res?.data?.observation_code?.sort((a, b) => a.order - b.order) || [];

      // Store configurable fields from API response
      this.configurableFields = res?.data?.configurable_fields || [];

      // Remove hidden fields from form after getting configurable_fields
      this.removeHiddenFieldsFromForm();

      const defaultType = this.observationTypes.find((item) => item.order === 1);
      if (defaultType) {
        this.observationForm.get('type')?.setValue(defaultType.value || null);
        this.setupCustomFields();
        this.getObservationCategories();
      } else {
        console.warn('No default observation type found with order === 1');
      }

      const defaultSignificance = this.observationSignificances.find((item) => item.order === 0);
      if (defaultSignificance) {
        this.observationForm.get('significance')?.setValue(defaultSignificance.value || null);
      }
    } catch (error) {
      console.error('Error fetching observation significances and types:', error);
    }
  }

  /**
   * Remove hidden fields from the form based on configurable_fields
   */
  removeHiddenFieldsFromForm(): void {
    if (!this.observationForm || !this.configurableFields || this.configurableFields.length === 0) {
      return;
    }

    this.configurableFields.forEach((fieldConfig) => {
      if (fieldConfig.visible === false && this.observationForm.get(fieldConfig.attribute)) {
        this.observationForm.removeControl(fieldConfig.attribute);
      }
    });
  }

  async getProjectActiveMembers(): Promise<void> {
    try {
      const res: any = await this.observationsService
        .projectActiveMembers(this._project.id)
        .toPromise();
      this.projectActiveMembers = res?.length ? res : res?.data;

      this.filteredUsers = this.projectActiveMembers;
    } catch (error) {
      this._toaster.error(
        error || this.translocoService.translate('Something went wrong, please try again.'),
      );
    }
  }

  /**
   * Load custom fields for the selected observation type
   * Handles empty responses and errors gracefully
   */
  setupCustomFields(): void {
    this.observationCustomFields
      .setupCustomFields(
        this.observationForm.get('type')?.value,
        this.observationForm,
        this.customFields$,
        this.isLoadingCustomFields$,
        (error) => {
          // Handle custom fields API error gracefully
          console.warn('Custom fields API error (non-critical):', error);
          // The form remains usable without custom fields
          // Ensure loading state is reset
          this.isLoadingCustomFields$.next(false);
        },
      )
      .subscribe();
  }

  /**
   * Get custom field values from the form
   * Returns empty object if custom fields are not available or empty
   */
  getCustomFieldValues(): any {
    if (!this.customFields$ || !this.customFields$.value || this.customFields$.value.length === 0) {
      return {};
    }

    return this.observationCustomFields.getCustomFieldValues(
      this.observationForm,
      this.customFields$.value,
    );
  }

  isDateValue(value: any): boolean {
    return this.observationCustomFields.isDateValue(value);
  }

  onObservationHashtagsChange(tags: string[]): void {
    this.observationHashtags = tags;
  }

  onSubmit(isDraft: boolean): void {
    if (this.isLoadingCustomFields$.value) {
      return;
    }

    if (!this.observationForm.valid && !isDraft) {
      return;
    }

    if (!this.validateAttachmentsBeforeSubmit(isDraft)) {
      return;
    }

    this.isLoading = true;
    const FORM_BODY = new FormData();

    // Get form values including disabled controls
    const formValues = this.observationForm.getRawValue();

    // Process form data including custom fields (handles empty array gracefully)
    const customFieldsValue = this.customFields$?.value || [];
    this.observationCustomFields.processFormDataForSubmission(
      this.observationForm,
      FORM_BODY,
      customFieldsValue,
    );

    this.fileToUpload ? FORM_BODY.append('screen_shot', this.fileToUpload) : null;
    // Handle category selection (single or multiple based on nwc_client)

    if (formValues.categories) {
      if (this.nwc_client) {
        // Single selection
        // need to make sure categoris is sent as array
        const categoriesArray = Array.isArray(formValues.categories)
          ? formValues.categories[0]
          : [formValues.categories];

        FORM_BODY.append('categories[]', categoriesArray);
      } else {
        // Multiple selection
        formValues.categories.forEach((categoryId: number) => {
          FORM_BODY.append('categories[]', categoryId.toString());
        });
      }
    }
    // Handle child category selection (single or multiple based on nwc_client)
    if (formValues.child_categories) {
      if (this.nwc_client) {
        // Single selection
        FORM_BODY.append('subcategories[]', formValues.child_categories);
      } else {
        // Multiple selection
        formValues.child_categories.forEach((categoryId: number) => {
          FORM_BODY.append('subcategories[]', categoryId.toString());
        });
      }
    }

    formValues.users?.map((user: any) => FORM_BODY.append('users[]', user));
    FORM_BODY.append('due_date', this.formattedDate);
    FORM_BODY.append('is_draft', isDraft.toString());
    this.attachments.map((file) => FORM_BODY.append('attached[]', file));

    this.observationsService
      .createObservation(appendObservationHashtagsToFormData(FORM_BODY, this.observationHashtags))
      .subscribe(
        (res) => {
          const message = res.message || this.translocoService.translate('Added Successfully');
          this.globalService.showTopMsg('success', message);
          this.isLoading = false;
          this.dialogRef.close('added');
        },
        (error) => {
          // {"status":"error","message":"Something went wrong"}
          if (error?.error?.errors) {
            error?.error?.errors?.forEach((errorMessage: string) => {
              this._toaster.error(errorMessage);
            });
          } else {
            this._toaster.error(error?.message);
          }

          this.isLoading = false;
        },
      );
  }

  get requiresAttachmentsForCurrentType(): boolean {
    return this.observationCustomFields.requiresAttachmentForCreation(
      this.observationForm?.value?.type,
      this.nwc_client,
    );
  }

  get isMissingRequiredAttachments(): boolean {
    return this.requiresAttachmentsForCurrentType && this.attachments.length === 0;
  }

  onAttachmentsChanged(files: File[]): void {
    this.attachments = files;
    this.attachmentsError = null;
  }

  private validateAttachmentsBeforeSubmit(isDraft: boolean): boolean {
    const validation = this.observationCustomFields.validateAttachmentsForCreation(
      this.observationForm?.getRawValue()?.type ?? this.observationForm?.value?.type,
      this.nwc_client,
      this.attachments,
      isDraft,
    );

    if (!validation.valid) {
      this.attachmentsError = validation.errorKey;
      return false;
    }

    this.attachmentsError = null;
    return true;
  }

  setPosition(e: MouseEvent | TouchEvent): void {
    let x, y;
    if (e instanceof MouseEvent) {
      x = e.offsetX;
      y = e.offsetY;
    } else if (e instanceof TouchEvent) {
      x = e.touches[0].clientX - this.myCanvas.nativeElement.offsetLeft;
      y = e.touches[0].clientY - this.myCanvas.nativeElement.offsetTop;
    }
    this.pos.x = x;
    this.pos.y = y;
  }

  default(): void {
    this.ctx.lineWidth = 1;
  }

  getDate(value: any): void {
    this.googleServices.setEvent('AddNewObservation_DueDate_project', 'Viewer Interactivity');

    const currentUserTime = moment(value).unix();
    const currentTimePlusHour = moment().add(0.99, 'hours').unix();
    if (currentUserTime <= currentTimePlusHour) {
      this._toaster.warning(
        this.translocoService.translate(
          'Due time must be at least more than one hour from current time!',
        ),
      );
      this.currentDueDate = new UntypedFormControl('');
    } else {
      //correcting time zone to Riyadh
      this.formattedDate = this.globalService.correctingTimeZone(value);
    }
  }

  checkMobile() {
    let ua = window.navigator.userAgent;
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(ua)
    )
      return true;
    else return false;
  }

  setGoogleEvent(eventAction: string): void {
    this.googleServices.setEvent(eventAction, 'Viewer Interactivity');
  }

  private _setMinDate() {
    this.minDate = new Date();
    this.minDate.setUTCHours(0, 0, 0, 0);
  }

  fileChangeEvent(event: any): void {
    const file = event.target?.files[0];
    this.fileAttached.nativeElement.value = '';
    if (!file) {
      return;
    } else {
      const mimeType = file.type;
      if (mimeType.match(/image\/*/) == null) {
        return;
      }
      const reader = new FileReader();
      const img = new Image();
      this.fileToUpload = file;

      img.onload = () => {};

      if (file && typeof file === 'string') {
        img.src = file;
        this.screenShotImage = file;
      } else {
        reader.readAsDataURL(file);
        reader.onload = (_event) => {
          this.screenShotImage = reader.result;
          img.src = this.screenShotImage;
        };
      }
    }
  }

  removeImg(): void {
    if (this.observationForm) {
      this.observationForm.get('screen_shot')?.reset();
    }
    this.fileToUpload = undefined;
    this.screenShotImage = undefined;
  }

  openConfirmationDialog() {
    const formValues = this.observationForm.getRawValue();
    const dialogData = new ConfirmDialogModel(
      this.translocoService.translate('Warning'),
      this.translocoService.translate(`You have unsaved changes. What would you like to do?`),
      false,
      false,
      null,
      this.translocoService.translate('Save Draft'),
      this.translocoService.translate('Cancel'),
      '',
      false,
      [],
      this.translocoService.translate('Keep Editing'),
      !(formValues.title && formValues.type),
      'heroicons_solid:exclamation',
    );

    const confirmDialog: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(
      ConfirmDialogComponent,
      {
        maxWidth: '640px',
        minWidth: '300px',
        data: dialogData,
        disableClose: true,
      },
    );

    confirmDialog.afterClosed().subscribe((result) => {
      if (result) {
        result === true ? this.onSubmit(true) : null;
      } else {
        this.dialogRef.close(false);
      }
    });
  }

  isArabic() {
    let currentLang = localStorage.getItem('lang');
    return currentLang === 'ar';
  }

  onEditorCreated(quill: Quill, editorType?: 'description' | 'recommendedAction') {
    if (!editorType) return;

    // Store the editor reference based on type
    if (editorType === 'description') {
      this.descriptionEditor = quill;
      // Apply initial disabled state for description editor
      setTimeout(() => this.updateQuillEditorState(), 0);
    } else if (editorType === 'recommendedAction') {
      this.recommendedActionEditor = quill;
    }

    // Setup editor using the service
    this.quillEditorService.setupEditor(quill, editorType, this.getEditorConfig());
  }

  get maxQuillDescriptionLength(): number | null {
    const observationType = this.observationForm?.get('type')?.value;
    if (!observationType) {
      return null;
    }
    const limit = this.quillEditorService.getDescriptionTextLimits(this.getEditorConfig());
    return limit;
  }

  get maxQuillEditorsHtmlLength(): number | null {
    const observationType = this.observationForm?.get('type')?.value;
    if (!observationType) {
      return null;
    }
    return this.quillEditorService.getDescriptionHtmlLimits(this.getEditorConfig());
  }

  get maxQuillRecommendedActionLength(): number | null {
    const observationType = this.observationForm?.get('type')?.value;
    if (!observationType) {
      return null;
    }
    return this.quillEditorService.getRecommendedActionTextLimits(this.getEditorConfig());
  }

  get screenshotPlaceholderPath(): string {
    return getUploadPlaceholderImage(this.nwc_client);
  }

  get maxQuillRecommendedActionHtmlLength(): number | null {
    const observationType = this.observationForm?.get('type')?.value;
    if (!observationType) {
      return null;
    }
    return this.quillEditorService.getRecommendedActionHtmlLimits(this.getEditorConfig());
  }

  private getEditorConfig(): EditorConfig {
    return {
      nwcClient: this.nwc_client,
      observationType: this.observationForm.get('type')?.value || '',
      form: this.observationForm,
      observationCustomFields: this.observationCustomFields,
    };
  }

  public isComponentReady(): boolean {
    return this.observationForm && !this.isLoading && !!this.data;
  }

  getDescriptionCharCount(): number | null {
    const observationType = this.observationForm?.get('type')?.value;
    if (!observationType) {
      return null;
    }
    return this.quillEditorService.getTextCharCount(
      this.descriptionEditor,
      'description',
      this.getEditorConfig(),
    );
  }

  getDescriptionHtmlCharCount(): number | null {
    const observationType = this.observationForm?.get('type')?.value;
    if (!observationType) {
      return null;
    }
    return this.quillEditorService.getHtmlCharCount(
      this.descriptionEditor,
      'description',
      this.getEditorConfig(),
    );
  }

  getRecommendedActionCharCount(): number | null {
    const observationType = this.observationForm?.get('type')?.value;
    if (!observationType) {
      return null;
    }
    return this.quillEditorService.getTextCharCount(
      this.recommendedActionEditor,
      'recommendedAction',
      this.getEditorConfig(),
    );
  }

  getRecommendedActionHtmlCharCount(): number | null {
    const observationType = this.observationForm?.get('type')?.value;
    if (!observationType) {
      return null;
    }
    return this.quillEditorService.getHtmlCharCount(
      this.recommendedActionEditor,
      'recommendedAction',
      this.getEditorConfig(),
    );
  }

  forceEditorLength(): void {
    if (this.descriptionEditor) {
      // Re-setup editor to update event handlers with new limits
      this.quillEditorService.setupEditor(
        this.descriptionEditor,
        'description',
        this.getEditorConfig(),
      );
      this.quillEditorService.forceEditorLength(
        this.descriptionEditor,
        'description',
        this.getEditorConfig(),
      );
    }

    if (this.recommendedActionEditor) {
      // Re-setup editor to update event handlers with new limits
      this.quillEditorService.setupEditor(
        this.recommendedActionEditor,
        'recommendedAction',
        this.getEditorConfig(),
      );
      this.quillEditorService.forceEditorLength(
        this.recommendedActionEditor,
        'recommendedAction',
        this.getEditorConfig(),
      );
    }
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();

    // Safely complete custom fields observables if they exist
    if (this.customFields$) {
      this.customFields$.complete();
    }
    if (this.isLoadingCustomFields$) {
      this.isLoadingCustomFields$.complete();
    }
  }

  get maxQuillEditorsLength(): number {
    return this.nwc_client ? 4000 : 1000;
  }
}
